// CR: Auto Mode Gating logic extracted for testability and diagnostic consistency
import type { StepGroupKey } from "./autoModeTypes";

export type StepGroupStatus =
  | "NOT_STARTED"
  | "RUNNING"
  | "COMPLETE"
  | "COMPLETE_WITH_TAIL"
  | "PARTIAL"
  | "FAILED"
  | "SKIPPED"
  | "PENDING"
  | "NEEDS_ACTION";

export interface S1SubstepStatusProvider {
  (substepKey: any): StepGroupStatus;
}

export interface GatingContext {
  getStepGroupStatus: (groupKey: any) => StepGroupStatus;
  sourcesEnabled: string[];
  requireS05ForFlyerS1?: boolean;
  s1StateSnapshot?: {
    isComplete: boolean;
    capture?: {
      terminal?: boolean;
      scope_frozen?: boolean;
    };
    blockedReasons: string[];
  } | null;
  s1SnapshotStabilityPhase?: string;
  getS1SubstepStatus?: S1SubstepStatusProvider;
  shouldBlockS4ForSnapshotSettling?: (phase: string, isComplete: boolean | null) => boolean;
}

/**
 * Derived blocked reason for a step group.
 * Returns null if the group is ready to run.
 */
export function getStepBlockedReason(
  groupKey: StepGroupKey,
  context: GatingContext,
  options?: { strict?: boolean }
): string | null {
  const { 
    getStepGroupStatus, 
    sourcesEnabled, 
    s1StateSnapshot, 
    s1SnapshotStabilityPhase,
    getS1SubstepStatus,
    shouldBlockS4ForSnapshotSettling
  } = context;
  
  const strict = options?.strict === true;
  const status = getStepGroupStatus(groupKey);
  
  if (status === "RUNNING") return "Step is currently running";

  if (groupKey === "S1") {
    const s05Status = getStepGroupStatus("S0.5");
    const flyerEnabled = sourcesEnabled.includes("FLYER");
    const requireS05ForFlyerS1 = context.requireS05ForFlyerS1 !== false;
    if (flyerEnabled && requireS05ForFlyerS1 && s05Status !== "COMPLETE" && s05Status !== "PARTIAL" && s05Status !== "SKIPPED") {
      return `Waiting for S0.5 (Download Capture) to complete (status: ${s05Status})`;
    }
    return null;
  }

  if (groupKey === "S4") {
    // S4 depends on S1 and S2
    const s1Status = getStepGroupStatus("S1");
    const s2Status = getStepGroupStatus("S2");

    const flyerEnabled = sourcesEnabled.includes("FLYER");
    const websiteEnabled = sourcesEnabled.includes("WEB");

    // Check S1 substeps if flyer enabled
    if (
      flyerEnabled &&
      s1Status !== "COMPLETE" &&
      s1Status !== "COMPLETE_WITH_TAIL" &&
      s1Status !== "PARTIAL" &&
      s1Status !== "SKIPPED"
    ) {
      if (getS1SubstepStatus) {
        const aiStatus = getS1SubstepStatus("S1_AI");
        const ocrStatus = getS1SubstepStatus("S1_OCR");
        const renderStatus = getS1SubstepStatus("S1_RENDER");
        const collectStatus = getS1SubstepStatus("S1_COLLECT");

        if (aiStatus === "RUNNING") return "Waiting for S1.3 (AI Candidates) to complete";
        if (aiStatus === "NOT_STARTED" && ocrStatus === "COMPLETE") return "Waiting for S1.3 (AI Candidates)";
        if (ocrStatus === "RUNNING") return "Waiting for S1.2 (OCR + Candidates) to complete";
        if (ocrStatus === "NOT_STARTED" && renderStatus === "COMPLETE") return "Waiting for S1.2 (OCR + Candidates)";
        if (renderStatus === "RUNNING") return "Waiting for S1.1 (Render Pages) to complete";
        if (renderStatus === "NOT_STARTED" && collectStatus === "COMPLETE") return "Waiting for S1.1 (Render Pages)";
        if (collectStatus === "RUNNING") return "Waiting for S1.0 (Collect Flyers) to complete";
        if (collectStatus === "NOT_STARTED") return "Waiting for S1.0 (Collect Flyers)";
      }
      return `Waiting for S1 (Flyer Extraction) to complete (status: ${s1Status})`;
    }

    if (strict && flyerEnabled && !s1StateSnapshot) {
      return "Waiting for canonical S1 state snapshot";
    }

    if (flyerEnabled && shouldBlockS4ForSnapshotSettling && s1SnapshotStabilityPhase) {
      const snapshotSettlingBlocksS4 = shouldBlockS4ForSnapshotSettling(
        s1SnapshotStabilityPhase,
        s1StateSnapshot?.isComplete ?? null
      );
      if (snapshotSettlingBlocksS4) {
        return strict ? "SNAPSHOT_SETTLING" : "Waiting for canonical S1 snapshot to stabilize";
      }
    }

    if (strict && flyerEnabled && s1StateSnapshot && s1StateSnapshot.capture?.terminal && s1StateSnapshot.capture?.scope_frozen === false) {
      return "Waiting for S1.0c frozen capture scope";
    }

    if (strict && flyerEnabled && s1StateSnapshot && !s1StateSnapshot.isComplete) {
      return s1StateSnapshot.blockedReasons[0] || "Waiting for canonical S1 completion gate";
    }

    if (
      websiteEnabled &&
      s2Status !== "COMPLETE" &&
      s2Status !== "COMPLETE_WITH_TAIL" &&
      s2Status !== "PARTIAL" &&
      s2Status !== "SKIPPED"
    ) {
      return `Waiting for S2 (Website Collect) to complete (status: ${s2Status})`;
    }

    return null;
  }

  if (groupKey === "S5") {
    const s4Status = getStepGroupStatus("S4");
    if (
      s4Status !== "COMPLETE" &&
      s4Status !== "COMPLETE_WITH_TAIL" &&
      s4Status !== "PARTIAL" &&
      s4Status !== "SKIPPED"
    ) {
      return `Waiting for S4 (Normalize) to complete (status: ${s4Status})`;
    }
    return null;
  }

  return null;
}
